import axios from 'axios';
import { BASE_URL_V4 } from '../constants/api';

const API_TIMEOUT_MS = 30000;
const DOWNLOAD_TIMEOUT_MS = 60000;

function scriptFileError(message, status = 0) {
    const error = new Error(message);
    // Preserve the error shape consumed by the editor's apiNotifications helper.
    error.response = { status, data: { type: 'error', title: message, content: [] } };
    return error;
}

function isMissingEndpoint(error) {
    // This is the old Laravel route-not-found response, not a missing script/object.
    return error.response?.status === 404
        && error.response?.data?.type === 'error'
        && error.response?.data?.title === 'The requested url was not found on this server.';
}

export async function getFileContentByKey(key) {
    const encodedKey = encodeURIComponent(key);
    let downloadUrl;
    try {
        const { data } = await axios.get(`${BASE_URL_V4}/v4/automation/file-url/${encodedKey}`, {
            timeout: API_TIMEOUT_MS,
        });
        downloadUrl = data?.content?.url;
    } catch (error) {
        if (isMissingEndpoint(error)) {
            // Support API hosts that have not received the new endpoint yet.
            try {
                const { data } = await axios.get(`${BASE_URL_V4}/v4/automation/read-file/${encodedKey}`, {
                    timeout: DOWNLOAD_TIMEOUT_MS,
                });
                return data.content.content;
            } catch (legacyError) {
                throw legacyError.response?.data ? legacyError
                    : scriptFileError('Cannot load script. Please try again.');
            }
        }
        throw error.response?.data ? error
            : scriptFileError('Cannot get script download URL. Please try again.');
    }

    if (typeof downloadUrl !== 'string' || !/^https?:\/\//i.test(downloadUrl)) {
        throw scriptFileError('Invalid script download URL.');
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS);
    try {
        // Bypass API interceptors: no Hidemium token, cookies or extra query parameters.
        const response = await fetch(downloadUrl, {
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            cache: 'no-store',
            signal: controller.signal,
        });
        if (!response.ok) {
            throw scriptFileError('Cannot download script. Please try again.', response.status);
        }
        return await response.text();
    } catch (error) {
        if (error.response?.data) throw error;
        throw scriptFileError(error.name === 'AbortError'
            ? 'Script download timed out. Please try again.'
            : 'Cannot download script. Please check your connection and try again.');
    } finally {
        clearTimeout(timeout);
    }
}
